/**
 * Invitations Service
 *
 * Gestiona el flujo de invitaciones de usuarios a una empresa.
 * Trabaja contra la tabla `invitations` (token, expires_at, etc.).
 * El envío del email lo delega a la edge function `send-invitation`.
 */

import { supabase } from '@shared/libs/supabaseClient';
import type { Invitation, Role } from '@shared/types/permissions';

export interface InviteUserRequest {
  empresaId: string;
  email: string;
  role: Role;
  message?: string;
}

export interface InvitationDetails {
  email: string;
  role: Role;
  empresaNombre?: string;
  expired: boolean;
  accepted: boolean;
}

export const invitationsService = {
  /** Crea una invitación y dispara el email vía edge function. */
  async inviteUser(req: InviteUserRequest, invitedById: string): Promise<Invitation> {
    // Insertar la invitación (el token y expires_at se autogeneran en BD).
    const { data, error } = await supabase
      .from('invitations')
      .insert({
        empresa_id: req.empresaId,
        email: req.email.toLowerCase(),
        role: req.role,
        invited_by: invitedById,
        message: req.message ?? null,
      })
      .select()
      .single();

    if (error) throw error;

    // Disparar el email (best-effort; la invitación ya queda creada).
    try {
      await supabase.functions.invoke('send-invitation', {
        body: { invitationId: (data as Invitation).id },
      });
    } catch (err) {
      console.warn('[invitationsService] No se pudo enviar el email de invitación:', err);
    }

    return data as Invitation;
  },

  /**
   * Obtiene los detalles de una invitación por token (vista pública).
   *
   * Usa la RPC `get_invitation_details` (SECURITY DEFINER) en lugar de un SELECT
   * con embed de `empresas`. El embed disparaba la policy RLS de `empresas` →
   * `company_users` → `check_user_in_empresa`, función que NO es ejecutable por
   * `anon` (ver 20260703_rbac_rls_unification.sql). Como el invitado abre el
   * link del email sin sesión (rol `anon`), esa consulta fallaba con
   * `permission denied` y la página mostraba "Invitación no encontrada" aunque
   * existiera. La RPC bypassa ese entrelazado de RLS.
   */
  async getByToken(token: string): Promise<InvitationDetails | null> {
    const { data, error } = await supabase.rpc('get_invitation_details', {
      p_token: token,
    });

    if (error || !data || !data.found) return null;

    return {
      email: data.email,
      role: data.role as Role,
      empresaNombre: data.empresaNombre ?? undefined,
      expired: !!data.expired,
      accepted: !!data.accepted,
    };
  },

  /**
   * Acepta una invitación vía RPC `accept_invitation` (SECURITY DEFINER).
   * La RPC valida token, expiración, coincide email, y hace UPSERT del
   * membership + marca la invitación como aceptada en una sola operación
   * atómica. Esto bypassa el RLS de company_users (que exigiría ser
   * owner/admin, imposible para un invitado que aún no es miembro).
   */
  async acceptInvitation(token: string): Promise<void> {
    const { data, error } = await supabase.rpc('accept_invitation', {
      p_token: token,
    });

    if (error) throw error;
    if (data && !data.success) {
      throw new Error(data.error || 'Error al aceptar la invitación.');
    }
  },

  /**
   * Rechaza una invitación (la elimina).
   *
   * Usa la RPC `decline_invitation` (SECURITY DEFINER) porque la policy DELETE
   * de `invitations` exige ser admin de la empresa, y el invitado todavía no
   * es miembro. El token es inadivinable (32 bytes random), así que quien lo
   * posee puede actuar sobre la invitación. Funciona con o sin sesión.
   */
  async declineInvitation(token: string): Promise<void> {
    const { data, error } = await supabase.rpc('decline_invitation', {
      p_token: token,
    });

    if (error) throw error;
    if (data && !data.success) {
      throw new Error(data.error || 'No se pudo rechazar la invitación.');
    }
  },

  /** Invitaciones pendientes de una empresa. */
  async getPendingInvitations(empresaId: string): Promise<Invitation[]> {
    const { data, error } = await supabase
      .from('invitations')
      .select('*')
      .eq('empresa_id', empresaId)
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[invitationsService] Error loading invitations:', error.message);
      return [];
    }
    return (data || []) as Invitation[];
  },

  /** Cancela una invitación pendiente. */
  async cancelInvitation(invitationId: string): Promise<void> {
    const { error } = await supabase.from('invitations').delete().eq('id', invitationId);
    if (error) throw error;
  },
};
